import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  type ZapIFrameConfig,
  ZapIFrameConfigMessageType,
  ZapIFrameReadyMessageType,
  type ZapIframeConfigMessage,
  type ZapMessage,
  type ZapTokenMessage,
  ZapTokenMessageType,
  ZapTokenRequestMessageType,
} from '@ctw/shared/types/zap-message';
import { isAuthTokenExpired } from '@ctw/shared/utils/auth';
import { debounce } from 'lodash-es';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';

interface ZapConfigContextValue {
  accessToken: string | undefined;
  zapConfig: ZapIFrameConfig | null;
  requestTokenRefresh: () => void;
}

const ZapConfigContext = createContext<ZapConfigContextValue | undefined>(undefined);

export function useZapConfigContext() {
  const context = useContext(ZapConfigContext);
  if (!context) {
    throw new Error('useZapConfigContext must be used within a ZapConfigProvider');
  }
  return context;
}

export function ZapConfigProvider({ children }: PropsWithChildren) {
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [messageConfig, setMessageConfig] = useState<ZapIFrameConfig | null>(null);
  const [searchParams] = useSearchParams();
  const telemetry = useTelemetry();

  // Parse config from URL search params
  const urlConfig = useMemo(() => {
    try {
      return JSON.parse(searchParams.get('config') ?? 'null') as ZapIFrameConfig;
    } catch (_error) {
      return null;
    }
  }, [searchParams]);

  const zapConfig = messageConfig ?? urlConfig;

  const postRefreshMessage = useCallback(() => {
    window.parent.postMessage({ type: ZapTokenRequestMessageType }, '*');
  }, []);

  const debouncedPostRefreshMessage = useMemo(
    () => debounce(postRefreshMessage, 10_000, { leading: true }),
    [postRefreshMessage],
  );

  // Send iframe ready message on mount
  useEffect(() => {
    window.parent.postMessage(
      {
        type: ZapIFrameReadyMessageType,
      },
      '*',
    );
    telemetry.logger.debug('sendOnIFrameReady: sent message to parent window');
  }, [telemetry]);

  // Handle all messages from parent window
  useEffect(() => {
    const handleMessage = (event: { data: ZapMessage }) => {
      const { data } = event;

      // Handle token messages
      if (data.type === ZapTokenMessageType) {
        setAccessToken((data as ZapTokenMessage).payload.accessToken);
        return;
      }

      if (data.type === ZapIFrameConfigMessageType) {
        setMessageConfig((data as ZapIframeConfigMessage).payload);
        return;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    return () => {
      debouncedPostRefreshMessage.cancel();
    };
  }, [debouncedPostRefreshMessage]);

  // Request token refresh on mount
  useEffect(() => {
    debouncedPostRefreshMessage();
  }, [debouncedPostRefreshMessage]);

  // Check if token is expired and request refresh
  useEffect(() => {
    if (isAuthTokenExpired(accessToken ?? '')) {
      debouncedPostRefreshMessage();
    }
  }, [accessToken, debouncedPostRefreshMessage]);

  const requestTokenRefresh = () => {
    debouncedPostRefreshMessage();
  };

  const contextValue: ZapConfigContextValue = {
    accessToken,
    zapConfig,
    requestTokenRefresh,
  };

  return <ZapConfigContext.Provider value={contextValue}>{children}</ZapConfigContext.Provider>;
}
